import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React from 'react';
import { randomString, _cs } from '@togglecorp/fujs';
import { IoClose, IoCalendarOutline } from '../../node_modules/react-icons/io5/index.esm.js';
import useUiModeClassName from '../../hooks/useUiModeClassName.js';
import useBlurEffect from '../../hooks/useBlurEffect.js';
import useBooleanState from '../../hooks/useBooleanState.js';
import useModalState from '../../hooks/useModalState.js';
import InputContainer from '../InputContainer/index.js';
import RawInput from '../RawInput/index.js';
import Button from '../Button/index.js';
import Popup from '../Popup/index.js';
import Modal from '../Modal/index.js';
import Calendar from '../Calendar/index.js';
import { genericMemo, ymdToDateString } from '../../utils/index.js';
import styles from './styles.css.js';

var _IoClose, _IoCalendarOutline;

function DateInput(props) {
  const {
    actions,
    actionsContainerClassName,
    className,
    disabled,
    error,
    errorContainerClassName,
    hint,
    hintContainerClassName,
    icons,
    iconsContainerClassName,
    inputSectionClassName,
    label,
    labelContainerClassName,
    readOnly,
    uiMode,
    inputElementRef,
    containerRef: containerRefFromProps,
    inputSectionRef,
    inputClassName,
    variant,
    onChange,
    name,
    value,
    showInModal,
    ...dateInputProps
  } = props;
  const [calendarMonthSelectionPopupClassName] = React.useState(randomString(16));
  const createdContainerRef = React.useRef(null);
  const popupRef = React.useRef(null);
  const containerRef = containerRefFromProps ?? createdContainerRef;
  const uiModeClassName = useUiModeClassName(uiMode, styles.light, styles.dark);
  const [showCalendar, setShowCalendarTrue, setShowCalendarFalse,, toggleShowCalendar] = useBooleanState(false);
  const [dateModalShown, setDateModalVisible, setDateModalHidden] = useModalState(false);
  const handleShowCalendar = React.useCallback(() => {
    if (showInModal) {
      setDateModalVisible();
    }

    toggleShowCalendar();
  }, [showInModal, setDateModalVisible, toggleShowCalendar]);
  const handleFocus = React.useCallback(() => {
    if (showInModal) {
      setDateModalVisible();
    }

    setShowCalendarTrue();
  }, [showInModal, setShowCalendarTrue, setDateModalVisible]);
  const handlePopupBlur = React.useCallback((isClickedOnPopup, isClickedOnParent, e) => {
    const isClickedWithin = isClickedOnPopup || isClickedOnParent; // Following is to prevent the popup blur when
    // month selection is changed in the calendar

    const container = document.getElementsByClassName(calendarMonthSelectionPopupClassName)[0];
    const isContainerOrInsideContainer = container ? container === e.target || container.contains(e.target) : false;

    if (!isClickedWithin && !isContainerOrInsideContainer) {
      setShowCalendarFalse();
    }
  }, [setShowCalendarFalse, calendarMonthSelectionPopupClassName]);
  useBlurEffect(showCalendar, handlePopupBlur, popupRef, containerRef);
  const handleCalendarDateClick = React.useCallback((year, month, day) => {
    if (onChange) {
      onChange(ymdToDateString(year, month, day), name);
    }

    setShowCalendarFalse();

    if (showInModal) {
      setDateModalHidden();
    }
  }, [name, onChange, setShowCalendarFalse, setDateModalHidden, showInModal]);
  const handleClearButtonClick = React.useCallback(() => {
    if (onChange) {
      onChange(undefined, name);
    }
  }, [onChange, name]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(InputContainer, {
    containerRef: containerRef,
    inputSectionRef: inputSectionRef,
    actions: /*#__PURE__*/React.createElement(React.Fragment, null, actions, !readOnly && /*#__PURE__*/React.createElement(React.Fragment, null, value && /*#__PURE__*/React.createElement(Button, {
      name: undefined,
      variant: "action",
      onClick: handleClearButtonClick,
      disabled: disabled,
      title: "Clear"
    }, _IoClose || (_IoClose = /*#__PURE__*/React.createElement(IoClose, null))), /*#__PURE__*/React.createElement(Button, {
      name: undefined,
      variant: "action",
      onClick: handleShowCalendar,
      title: "Set current date",
      disabled: disabled
    }, _IoCalendarOutline || (_IoCalendarOutline = /*#__PURE__*/React.createElement(IoCalendarOutline, null))))),
    actionsContainerClassName: actionsContainerClassName,
    className: className,
    disabled: disabled,
    error: error,
    errorContainerClassName: errorContainerClassName,
    hint: hint,
    hintContainerClassName: hintContainerClassName,
    icons: icons,
    iconsContainerClassName: iconsContainerClassName,
    inputSectionClassName: inputSectionClassName,
    label: label,
    labelContainerClassName: labelContainerClassName,
    readOnly: readOnly,
    uiMode: uiMode,
    variant: variant,
    input: /*#__PURE__*/React.createElement(RawInput, _extends({}, dateInputProps, {
      name: name,
      className: _cs(styles.input, uiModeClassName, !!error && styles.errored, !value && styles.empty, inputClassName),
      elementRef: inputElementRef // NOTE: Make this required to hide clear button on firefox
      ,
      required: !!value,
      readOnly: true,
      uiMode: uiMode,
      disabled: disabled,
      value: value,
      onFocus: handleFocus,
      type: "date"
    }))
  }), !readOnly && !showInModal && /*#__PURE__*/React.createElement(Popup, {
    parentRef: containerRef,
    elementRef: popupRef,
    show: showCalendar,
    freeWidth: true,
    className: styles.calendarPopup,
    contentClassName: styles.popupContent
  }, /*#__PURE__*/React.createElement(Calendar, {
    onDateClick: handleCalendarDateClick,
    className: styles.calendar,
    monthSelectionPopupClassName: calendarMonthSelectionPopupClassName,
    initialDate: value ?? undefined,
    activeDate: value ?? undefined
  })), !readOnly && showInModal && dateModalShown && /*#__PURE__*/React.createElement(Modal, {
    onCloseButtonClick: setDateModalHidden,
    className: styles.calendarModal,
    spacing: "compact",
    size: "free",
    freeHeight: true
  }, /*#__PURE__*/React.createElement(Calendar, {
    onDateClick: handleCalendarDateClick,
    className: styles.calendar,
    monthSelectionPopupClassName: calendarMonthSelectionPopupClassName,
    initialDate: value ?? undefined,
    activeDate: value ?? undefined
  })));
}

var index = genericMemo(DateInput);

export { index as default };
//# sourceMappingURL=index.js.map
